/**
 * Cliente de la API de Clientes (tag `clientes-flotas` de `openapi.yaml`) — Admin Web.
 *
 * Endpoints (US-2.3):
 * - GET   /api/v1/admin/customers
 * - POST  /api/v1/admin/customers
 * - GET   /api/v1/admin/customers/{customerId}
 * - PATCH /api/v1/admin/customers/{customerId}
 * - POST  /api/v1/admin/customers/{customerId}/balance-adjustments
 *
 * Tipos derivados del contrato en `@goelectric/shared-types`; mismo patrón de red
 * que `allyApi`/`locationApi` (sobre `apiRequest`).
 */
import type {
  AdjustCustomerBalanceBody,
  BalanceAdjustmentResponse,
  CreateCustomerBody,
  Customer,
  CustomersPage,
  ListCustomersParams,
  UpdateCustomerBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado (offset/limit + filtros), tal como los define el contrato. */
export type CustomerListParams = ListCustomersParams;

/**
 * Genera la `Idempotency-Key` de un ajuste de saldo. El endpoint la exige para no
 * aplicar dos veces el mismo movimiento ante reintentos. `crypto.randomUUID()` requiere
 * contexto seguro (HTTPS/localhost), el mismo requisito que ya tiene el resto de la PWA.
 */
export function createIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: CustomerListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const customerApi = {
  list(params: CustomerListParams = {}): Promise<CustomersPage> {
    return apiRequest<CustomersPage>(`/api/v1/admin/customers${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(customerId: string): Promise<Customer> {
    return apiRequest<Customer>(`/api/v1/admin/customers/${encodeURIComponent(customerId)}`);
  },

  create(body: CreateCustomerBody): Promise<Customer> {
    return apiRequest<Customer>('/api/v1/admin/customers', { method: 'POST', body });
  },

  update(customerId: string, body: UpdateCustomerBody): Promise<Customer> {
    return apiRequest<Customer>(`/api/v1/admin/customers/${encodeURIComponent(customerId)}`, {
      method: 'PATCH',
      body,
    });
  },

  /**
   * Aplica un ajuste de saldo auditable (monto con signo + motivo). Es el endpoint
   * dedicado del contrato: el saldo NUNCA se modifica por el PATCH del cliente.
   *
   * La `Idempotency-Key` se resuelve UNA vez por llamada (reutilizando la que recibe del
   * llamador o generando una nueva con `createIdempotencyKey`). Así los reintentos
   * internos de `apiRequest` —p. ej. el refresh de sesión ante un `401`— reenvían la
   * MISMA clave y el backend no duplica el movimiento.
   */
  adjustBalance(
    customerId: string,
    body: AdjustCustomerBalanceBody,
    idempotencyKey: string = createIdempotencyKey(),
  ): Promise<BalanceAdjustmentResponse> {
    return apiRequest<BalanceAdjustmentResponse>(
      `/api/v1/admin/customers/${encodeURIComponent(customerId)}/balance-adjustments`,
      { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } },
    );
  },
};
